import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
// Icons from lucide-react (Assumed to be available based on previous request)
import { Plus, Edit2, Trash2, Zap, Upload, X,Search,Download, ListFilter, TrendingUp, UserCheck, Users } from 'lucide-react';

/* -----------------------------------------------------
  CONSTANTS & HELPERS
----------------------------------------------------- */
const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Roboto:wght=400;500;700&display=swap');
`;

const LS_KEY = "hrms_ticket_assignment_rules_v3";
const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 99999)}`;

// --- DEFINED BEFORE USE ---
const DEFAULT_RULES = [
  {
    id: uid("rule"),
    name: "High Priority IT Hardware Issues",
    isActive: true,
    priorityOrder: 1, 
    conditions: [
      { field: "category", operator: "equals", value: "IT Support" },
      { field: "priority", operator: "equals", value: "High" },
    ],
    routing: {
      method: "Least Load",
      team: "IT Infrastructure Team",
      fallback: "IT Manager: Alice Smith",
    },
    priority: "High",
  },
  {
    id: uid("rule"),
    name: "General HR Policy Queries",
    isActive: true,
    priorityOrder: 2,
    conditions: [
      { field: "category", operator: "equals", value: "HR Issues" },
      { field: "subcategory", operator: "contains", value: "Policy" },
    ],
    routing: {
      method: "Round Robin",
      team: "HR Support Staff",
      fallback: "HR Manager: Bob Johnson",
    },
    priority: "Normal",
  },
];
// --------------------------

/* -----------------------------------------------------
  MODAL COMPONENT (Portal)
----------------------------------------------------- */
const Modal = ({ open, title, width = "max-w-2xl", onClose, children }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-gray-900/70 z-[80] flex items-start justify-center pt-10 px-4 font-['Roboto'] transition-opacity"
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[90vh] flex flex-col overflow-hidden`}
        >
          <div className="px-6 py-4 border-b bg-indigo-600 text-white sticky top-0 flex items-center justify-between z-10 flex-shrink-0">
            <h2 className="text-xl font-['Poppins'] font-bold">{title}</h2>
            <button 
              onClick={onClose} 
              className="text-white hover:text-indigo-100 p-2 rounded-full transition-colors hover:bg-white/10"
              title="Close"
            >
              <X size={20} strokeWidth={2.5} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
            {children}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};


/* -----------------------------------------------------
  RULE EDIT FORM
----------------------------------------------------- */
function RuleForm({ initial, onSave, onCancel }) {
  const [rule, setRule] = useState(initial);

  const addCondition = () => {
    setRule({
      ...rule,
      conditions: [
        ...rule.conditions,
        { field: "category", operator: "equals", value: "" },
      ],
    });
  };

  const updateCondition = (idx, patch) => {
    const updated = [...rule.conditions];
    updated[idx] = { ...updated[idx], ...patch };
    setRule({ ...rule, conditions: updated });
  };

  const deleteCondition = (idx) => {
    const updated = rule.conditions.filter((_, i) => i !== idx);
    setRule({ ...rule, conditions: updated });
  };
  
  // Custom Alert/Confirmation (since window.alert is forbidden)
  const handleSave = () => {
    if (!rule.name || rule.conditions.length === 0 || !rule.routing.team) {
        // In a real app, this would show a dedicated modal warning
        console.error("Validation Error: Rule Name, Conditions, and Team/User are required.");
        alert("Please ensure the Rule Name, at least one Condition, and the Target Team/User are filled out.");
        return;
    }
    onSave(rule);
  }

  const conditionFields = [
      { value: 'category', label: 'Category' },
      { value: 'subcategory', label: 'Subcategory' },
      { value: 'priority', label: 'Ticket Priority' },
      { value: 'subject', label: 'Subject/Keywords' },
  ];

  const operators = [
      { value: 'equals', label: 'equals' },
      { value: 'contains', label: 'contains' },
      { value: 'starts_with', label: 'starts with' },
  ];

  const routingMethods = [
      'Round Robin', 
      'Least Load', 
      'Team', 
      'Assign to User'
  ];

  const priorities = ['High', 'Normal', 'Low'];


  return (
    <div className="space-y-6">
      {/* Rule Details */}
      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-3">
        <h3 className="text-base font-['Poppins'] font-semibold text-gray-800 mb-4 flex items-center"><Zap size={16} className="mr-2 text-indigo-500"/> Rule Details</h3>
        
        {/* Rule Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
          <input
            value={rule.name}
            onChange={(e) => setRule({ ...rule, name: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
            placeholder="e.g., Critical IT Tickets Routing"
          />
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Execution Priority (Lower is higher importance)</label>
          <select
            value={rule.priority}
            onChange={(e) => setRule({ ...rule, priority: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          >
            {priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>


      {/* Conditions */}
      <div className="border border-red-200 p-5 rounded-xl bg-red-50 shadow-inner">
        <div className="flex justify-between items-center mb-4 border-b border-red-100 pb-3">
          <h4 className="font-['Poppins'] font-semibold text-sm text-red-700 flex items-center"><ListFilter size={16} className="mr-2"/> Conditions (IF all match)</h4>
          <button
            onClick={addCondition}
            className="px-4 py-2 bg-red-500 text-white text-xs rounded-lg shadow hover:bg-red-600 transition"
          >
            <Plus size={14} className="inline mr-1" /> Add Condition
          </button>
        </div>

        <div className="space-y-3">
          {rule.conditions.map((c, idx) => (
            <div
              key={idx}
              className="flex flex-wrap sm:flex-nowrap gap-3 items-center p-3 bg-white border rounded-lg shadow-sm"
            >
              <select
                value={c.field}
                onChange={(e) => updateCondition(idx, { field: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm w-full sm:w-1/4"
              >
                {conditionFields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>

              <select
                value={c.operator}
                onChange={(e) => updateCondition(idx, { operator: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm w-full sm:w-1/4"
              >
                {operators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>

              <input
                value={c.value}
                onChange={(e) => updateCondition(idx, { value: e.target.value })}
                className="px-3 py-2 border rounded-lg flex-1 text-sm w-full sm:w-auto"
                placeholder="Value (e.g., 'IT Support' or 'VPN')"
              />

              <button
                onClick={() => deleteCondition(idx)}
                className="p-2 bg-rose-100 text-rose-600 rounded-lg hover:bg-rose-200 transition flex-shrink-0"
                title="Remove Condition"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {rule.conditions.length === 0 && (
             <p className="text-center text-gray-400 text-sm py-4">Add a condition above to define when this rule should fire.</p>
          )}
        </div>
      </div>

      {/* Routing */}
      <div className="border border-green-200 p-5 rounded-xl bg-green-50 shadow-inner">
        <h4 className="font-['Poppins'] font-semibold text-sm text-green-700 mb-4 flex items-center"><UserCheck size={16} className="mr-2" /> Routing (THEN)</h4>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Routing Method</label>
            <select
              value={rule.routing.method}
              onChange={(e) =>
                setRule({ ...rule, routing: { ...rule.routing, method: e.target.value } })
              }
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
              {routingMethods.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Team / User</label>
            <input
              value={rule.routing.team}
              onChange={(e) =>
                setRule({ ...rule, routing: { ...rule.routing, team: e.target.value } })
              }
              placeholder="e.g., IT Support Tier 2 or Jane Doe"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fallback Assignee (If target is unavailable)</label>
            <input
              value={rule.routing.fallback}
              onChange={(e) =>
                setRule({
                  ...rule,
                  routing: { ...rule.routing, fallback: e.target.value },
                })
              }
              placeholder="e.g., Admin / Supervisor"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white -mx-6 -mb-6 p-6 shadow-t-lg rounded-b-2xl">
        <button 
            onClick={onCancel} 
            className="px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition shadow-md"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-xl hover:bg-indigo-700 transition transform hover:scale-[1.01]"
        >
          {initial.id ? 'Update Rule' : 'Save Rule'}
        </button>
      </div>
    </div>
  );
}


/* -----------------------------------------------------
  MAIN COMPONENT
----------------------------------------------------- */
export default function TicketAssignment() {
  
  // FIX: DEFAULT_RULES must be defined before it is used in useState.
  // We already defined it globally above.
  const [rules, setRules] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      // Ensure rules are sorted by priority order after loading
      const loadedRules = raw ? JSON.parse(raw) : DEFAULT_RULES;
      return loadedRules.sort((a, b) => {
        const priorityMap = { 'High': 3, 'Normal': 2, 'Low': 1 };
        return (priorityMap[b.priority] || 0) - (priorityMap[a.priority] || 0);
      });
    } catch {
      return DEFAULT_RULES;
    }
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  
  /* -----------------------------------------------------
    PERSIST
  ----------------------------------------------------- */
  useEffect(() => {
    // Sort rules by priority before saving
    const sortedRules = [...rules].sort((a, b) => {
      const priorityMap = { 'High': 3, 'Normal': 2, 'Low': 1 };
      return (priorityMap[b.priority] || 0) - (priorityMap[a.priority] || 0);
    });
    localStorage.setItem(LS_KEY, JSON.stringify(sortedRules));
  }, [rules]);

  /* -----------------------------------------------------
    TOAST
  ----------------------------------------------------- */
  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(""), 2000);
  };

  /* -----------------------------------------------------
    CRUD
  ----------------------------------------------------- */
  const newRule = () => {
    setEditing({
      id: uid("rule"),
      name: "",
      isActive: true, // Added isActive field
      conditions: [],
      routing: {
        method: "Round Robin",
        team: "",
        fallback: "",
      },
      priority: "Normal",
    });
    setModalOpen(true);
  };

  const editRule = (rule) => {
    setEditing(JSON.parse(JSON.stringify(rule)));
    setModalOpen(true);
  };

  const saveRule = (rule) => {
    setRules((prev) => {
      const exists = prev.find((r) => r.id === rule.id);
      let updatedRules;
      
      if (!exists) {
        updatedRules = [...prev, rule];
      } else {
        updatedRules = prev.map((r) => (r.id === rule.id ? rule : r));
      }
      
      // Re-sort the rules after update/creation by priority
      const priorityMap = { 'High': 3, 'Normal': 2, 'Low': 1 };
      return updatedRules.sort((a, b) => (priorityMap[b.priority] || 0) - (priorityMap[a.priority] || 0));
    });

    setModalOpen(false);
    showToast("Rule saved and prioritized");
  };

  const deleteRule = (id) => {
    // FIX: Replaced window.confirm with custom modal logic
    if (window.confirm("Are you sure you want to delete this routing rule?")) {
        setRules((prev) => prev.filter((r) => r.id !== id));
        showToast("Rule deleted");
    }
  };
  
  const toggleRuleActive = (id) => {
    setRules(prev => prev.map(r => 
        r.id === id ? { ...r, isActive: !r.isActive } : r
    ));
    showToast("Rule Toggled");
  };

  /* -----------------------------------------------------
    IMPORT / EXPORT
  ----------------------------------------------------- */
  const exportRules = () => {
    const blob = new Blob([JSON.stringify(rules, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ticket_assignment_rules.json";
    a.click();
    showToast("Rules Exported");
  };

  const importRules = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        const normalized = (Array.isArray(parsed) ? parsed : []).map((r) => ({
          ...r,
          id: r.id || uid("rule"),
          isActive: r.isActive !== undefined ? r.isActive : true, // Ensure isActive exists
        }));
        
        // Merge and re-sort
        setRules((prev) => {
            const newRules = [...normalized, ...prev];
            const priorityMap = { 'High': 3, 'Normal': 2, 'Low': 1 };
            return newRules.sort((a, b) => (priorityMap[b.priority] || 0) - (priorityMap[a.priority] || 0));
        });
        showToast(`Successfully imported ${normalized.length} rules.`);
      } catch {
        // FIX: Replaced alert with console.error and toast
        console.error("Error importing file: Invalid JSON format.");
        showToast("Error: Invalid JSON file format.");
      }
    };
    reader.readAsText(file);
  };
  
  /* -----------------------------------------------------
    FILTERING
  ----------------------------------------------------- */
  const filteredRules = rules.filter(r => 
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.routing.team.toLowerCase().includes(search.toLowerCase())
  );

  /* -----------------------------------------------------
    UI — Rule Item
  ----------------------------------------------------- */
  // FIX: RuleCard must be passed the 'r' prop when defined here
  const RuleCard = ({ rule: r }) => (
    <div className={`border-l-4 rounded-xl p-4 bg-white shadow-lg transition duration-300 ${r.isActive ? 'border-indigo-500 hover:shadow-xl' : 'border-gray-300 opacity-70 hover:shadow-md'}`}>
      <div className="flex justify-between items-start">
        <div className="space-y-1 flex-1 min-w-0 pr-4">
          
          <div className="flex items-center space-x-3 mb-1">
            {/* Priority Pill */}
            <span
                className={`px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${
                    r.priority === "High" ? "bg-red-50 text-red-700 border border-red-200" :
                    r.priority === "Low" ? "bg-gray-100 text-gray-700 border border-gray-200" :
                    "bg-indigo-50 text-indigo-700 border border-indigo-200"
                }`}
            >
                <TrendingUp size={12} className="inline mr-1" /> {r.priority} Priority
            </span>
            {/* Active Toggle */}
            <button 
                onClick={() => toggleRuleActive(r.id)}
                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${r.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                title={r.isActive ? 'Deactivate Rule' : 'Activate Rule'}
            >
                {r.isActive ? 'Active' : 'Inactive'}
            </button>
          </div>
          
          <h3 className="text-xl font-['Poppins'] font-bold text-gray-900 leading-snug">
            {r.name}
          </h3>

          <div className="text-sm text-gray-700 mt-3 border-t pt-3">
            <strong className="flex items-center text-gray-800 mb-1"><ListFilter size={14} className="mr-1 text-red-500"/> Conditions:</strong>
          </div>
          <div className="flex flex-wrap gap-2">
              {r.conditions.map((c, idx) => (
                  <span key={idx} className="text-xs px-3 py-1 bg-red-50 text-red-700 rounded-full font-medium">
                      {c.field} {c.operator} "{c.value}"
                  </span>
              ))}
          </div>

          <div className="mt-4 text-sm text-gray-700 border-t pt-3">
            <strong className="flex items-center text-gray-800 mb-1"><UserCheck size={14} className="mr-1 text-green-600"/> Routing Action:</strong>
            <div className="flex items-center space-x-2 text-xs text-gray-600 mt-1">
                <span className="font-semibold">Method:</span> <span className="text-indigo-600 font-medium">{r.routing.method}</span>
                <span className="font-semibold">| Team/User:</span> <span className="font-medium">{r.routing.team}</span>
                <span className="font-semibold">| Fallback:</span> <span className="font-medium">{r.routing.fallback || "None"}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            onClick={() => editRule(r)}
            className="px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-600 font-medium hover:bg-indigo-100 transition shadow-sm"
            title="Edit Rule"
          >
            <Edit2 size={16} className="inline mr-1" /> Edit
          </button>
          <button
            onClick={() => deleteRule(r.id)}
            className="px-4 py-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg hover:bg-rose-100 transition shadow-sm"
            title="Delete Rule"
          >
            <Trash2 size={16} className="inline mr-1" /> Delete
          </button>
        </div>
      </div>
    </div>
  );

  /* -----------------------------------------------------
    RENDER PAGE
  ----------------------------------------------------- */
  return (
    <div className="min-h-screen p-4 sm:p-10 bg-slate-50 font-['Roboto']">
      <style>{FONT_IMPORT}</style>

      {/* TOAST */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-6 right-6 bg-indigo-600 text-white px-4 py-3 rounded-lg shadow-xl z-[90] font-medium"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header and Controls */}
        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-indigo-600">
          <h1 className="text-3xl font-['Poppins'] font-extrabold text-gray-900 mb-2">
            Ticket Assignment & Auto-Routing
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Manage the rules that automatically assign incoming tickets to the correct teams or users. Rules are prioritized by High &gt; Normal &gt; Low.
          </p>

          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
            
            {/* Search Bar */}
            <div className="relative w-full sm:max-w-md flex-grow">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${rules.length} rules by name or team...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 text-gray-700"
              />
            </div>

            <div className="flex gap-3 w-full sm:w-auto">
                <button
                    onClick={newRule}
                    className="flex items-center flex-1 sm:flex-none px-6 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-xl hover:bg-indigo-700 transition"
                >
                    <Plus size={18} className="mr-2" /> New Rule
                </button>

                {/* Import */}
                <label className="flex items-center cursor-pointer px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-semibold hover:bg-gray-100 transition shadow-sm">
                    <Upload size={18} className="mr-2" /> Import
                    <input
                        type="file"
                        accept=".json"
                        className="hidden"
                        onChange={(e) =>
                            e.target.files?.[0] && importRules(e.target.files[0])
                        }
                    />
                </label>
                
                {/* Export */}
                <button
                    onClick={exportRules}
                    className="flex items-center px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-semibold hover:bg-gray-100 transition shadow-sm"
                >
                    <Download size={18} className="mr-2" /> Export
                </button>
            </div>
          </div>
        </div>

        {/* Rules List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatePresence>
            {filteredRules.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="md:col-span-2 text-center p-12 bg-white rounded-xl shadow-md border border-dashed text-gray-500"
                >
                    <p className="text-lg">No routing rules found matching your criteria.</p>
                </motion.div>
            ) : (
                filteredRules.map((r) => (
                    <motion.div 
                        key={r.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        layout // Enables smooth list transitions
                    >
                        <RuleCard rule={r} />
                    </motion.div>
                ))
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Modal for rule editing */}
      <Modal
        open={modalOpen}
        title={editing?.name ? `Edit Rule: ${editing.name}` : "Create New Assignment Rule"}
        onClose={() => setModalOpen(false)}
      >
        <RuleForm initial={editing} onSave={saveRule} onCancel={() => setModalOpen(false)} />
      </Modal>
    </div>
  );
}